import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Users, Plus, Trash2, Wand2, ShieldCheck, AlertTriangle, RefreshCw, Loader2, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { HoloCard, NeonButton, accentColor } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadGroupReport } from "@/lib/report";
import { useAuth } from "@/context/AuthContext";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const ACC = "yellow";

function Boxes({ image, faces, names }) {
  return (
    <div className="relative inline-block max-w-full" data-testid="face-boxes">
      <img src={image} alt="group" className="max-w-full max-h-[420px] object-contain" />
      {faces.map((f) => {
        const [x1, y1, x2, y2] = f.bbox_pct;
        return (
          <div key={f.index} className="absolute border-2 flex items-start justify-start"
            style={{ left: `${x1 * 100}%`, top: `${y1 * 100}%`, width: `${(x2 - x1) * 100}%`, height: `${(y2 - y1) * 100}%`, borderColor: "#eaff00", boxShadow: "0 0 12px rgba(234,255,0,0.5)" }}
            data-testid={`face-box-${f.index}`}>
            <span className="bg-[#eaff00] text-black font-display font-black text-xs px-1.5">{f.index + 1}{names && names[f.index] ? ` ${names[f.index]}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

const RELATIONSHIPS = ["", "mother", "father", "daughter", "son", "grandmother", "grandfather", "sister", "brother", "partner", "friend", "coworker", "child"];

export default function GroupForge() {
  const { refreshUser } = useAuth();
  const c = accentColor(ACC);
  const [view, setView] = useState("list"); // list | create | detail
  const [groups, setGroups] = useState([]);
  const [detail, setDetail] = useState(null);

  // create state
  const [srcImage, setSrcImage] = useState(null);
  const [faces, setFaces] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [gname, setGname] = useState("");
  const [people, setPeople] = useState({}); // index -> {name, relationship, is_child}
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  // generate state
  const [opts, setOpts] = useState(null);
  const [mode, setMode] = useState("family");
  const [layout, setLayout] = useState("auto");
  const [prompt, setPrompt] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fixing, setFixing] = useState(null);

  const loadGroups = useCallback(async () => {
    try { const { data } = await api.get("/groups"); setGroups(data); } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadGroups(); api.get("/ai/group-options").then(({ data }) => setOpts(data)).catch(() => {}); }, [loadGroups]);

  const pickSource = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const img = await fileToDataUrl(f);
    setSrcImage(img); setFaces([]); setPeople({}); setDetecting(true);
    try {
      const { data } = await api.post("/groups/detect", { image: img });
      setFaces(data.faces || []);
      if (!data.faces?.length) toast.error("No faces detected — try a clearer group photo");
      else toast.success(`${data.faces.length} people detected`);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Detection failed"); }
    finally { setDetecting(false); }
  };

  const setPerson = (i, k, v) => setPeople((p) => ({ ...p, [i]: { ...(p[i] || {}), [k]: v } }));

  const createGroup = async () => {
    if (!gname.trim()) return toast.error("Group name required");
    const list = faces.map((f) => ({ index: f.index, name: (people[f.index]?.name || "").trim(), relationship: people[f.index]?.relationship || "", is_child: !!people[f.index]?.is_child }))
      .filter((p) => p.name);
    if (!list.length) return toast.error("Name at least one person");
    setSaving(true);
    try {
      await api.post("/groups", { name: gname, source_image: srcImage, people: list });
      toast.success("Group identity profiles locked");
      setView("list"); setSrcImage(null); setFaces([]); setPeople({}); setGname(""); loadGroups();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Failed to create group"); }
    finally { setSaving(false); }
  };

  const openDetail = async (g) => {
    try { const { data } = await api.get(`/groups/${g.id}`); setDetail(data); setResult(data.generations?.[0] || null); setView("detail"); }
    catch (e) { toast.error("Failed to load group"); }
  };

  const removeGroup = async (id) => {
    try { await api.delete(`/groups/${id}`); toast.success("Group deleted"); loadGroups(); } catch (e) { toast.error("Delete failed"); }
  };

  const generate = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post(`/groups/${detail.id}/generate`, { mode, layout, prompt });
      setResult(data.generation);
      await refreshUser();
      const r = data.generation.report;
      if (r.status === "LOCKED") toast.success(`Group locked · ${r.group_score}%`);
      else toast.warning(`Group score ${r.group_score}% — issues flagged${data.generation.refunded ? " · credits refunded" : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Generation failed"); }
    finally { setGenLoading(false); }
  };

  const fixPerson = async (idx) => {
    setFixing(idx);
    try {
      const { data } = await api.post(`/groups/${detail.id}/regenerate-person`, { generation_id: result.id, person_index: idx, prompt: "" });
      setResult(data.generation); await refreshUser();
      toast[data.person_fixed?.status === "LOCKED" ? "success" : "warning"](`${data.person_fixed?.name}: ${data.person_fixed?.score}%${data.refunded ? " · refunded" : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Regeneration failed"); }
    finally { setFixing(null); }
  };

  // ---- LIST ----
  if (view === "list") {
    return (
      <div data-testid="module-groupforge">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-black text-3xl">Multi-Identity Orchestration™</h1>
            <p className="font-mono-tech text-white/50 text-sm mt-1">Preserve every person in couples, families & group portraits</p>
          </div>
          <NeonButton accent={ACC} onClick={() => setView("create")} data-testid="new-group-btn"><Plus size={14} /> New Group</NeonButton>
        </div>
        {groups.length === 0 ? (
          <div className="font-mono-tech text-white/30 text-sm border border-white/10 border-dashed p-12 text-center">No groups yet. Upload a family or group photo to begin.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {groups.map((g) => (
              <HoloCard key={g.id} accent={ACC} className="p-5" testid={`group-${g.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-lg truncate">{g.name}</h3>
                  <button onClick={() => removeGroup(g.id)} data-testid={`delete-group-${g.id}`} className="text-white/30 hover:text-neon-pink"><Trash2 size={14} /></button>
                </div>
                <div className="flex items-center gap-2 mt-2 font-mono-tech text-[11px] text-white/50"><Users size={12} /> {g.person_count} people</div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {g.people.map((p) => (
                    <div key={p.index} className="border border-white/10 p-0.5" title={p.name}>
                      {p.crop ? <img src={p.crop} alt={p.name} className="w-9 h-9 object-cover" /> : <div className="w-9 h-9 bg-white/5" />}
                    </div>
                  ))}
                </div>
                <NeonButton accent={ACC} className="w-full mt-4" onClick={() => openDetail(g)} data-testid={`open-group-${g.id}`}>Open Orchestrator</NeonButton>
              </HoloCard>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- CREATE ----
  if (view === "create") {
    return (
      <div data-testid="group-create">
        <button onClick={() => setView("list")} className="flex items-center gap-2 font-mono-tech text-white/50 text-xs mb-6 hover:text-white"><ArrowLeft size={14} /> Back</button>
        <HoloCard accent={ACC} className="p-6">
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Group Name</span>
          <input value={gname} onChange={(e) => setGname(e.target.value)} data-testid="group-name-input" className="mt-1 w-full bg-black/30 border-b border-white/15 focus:outline-none px-1 py-2 text-white font-mono-tech text-sm" placeholder="The Johnson Family" />

          <div className="mt-6">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Group Photo</span>
            <div onClick={() => fileRef.current?.click()} data-testid="group-upload-zone"
              className="mt-2 border border-dashed border-white/20 p-4 flex items-center justify-center cursor-pointer hover:border-neon-yellow transition-colors min-h-[200px]">
              {detecting ? <div className="flex items-center gap-2 text-white/50 font-mono-tech text-xs"><Loader2 size={16} className="animate-spin" /> Detecting faces…</div>
                : srcImage ? <Boxes image={srcImage} faces={faces} names={Object.fromEntries(Object.entries(people).map(([i, v]) => [i, v.name]))} />
                  : <div className="text-center text-white/40 font-mono-tech text-xs"><Upload size={22} className="mx-auto mb-2" /> Click to upload a group photo</div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickSource} />
          </div>

          {faces.length > 0 && (
            <div className="mt-6 space-y-3" data-testid="people-inputs">
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Name each person · they become separate identity profiles</span>
              {faces.map((f) => (
                <div key={f.index} className="flex flex-wrap items-center gap-3 border border-white/10 p-3" data-testid={`person-row-${f.index}`}>
                  {f.crop && <img src={f.crop} alt="" className="w-12 h-12 object-cover border border-white/15" />}
                  <span className="font-display font-black text-neon-yellow">#{f.index + 1}</span>
                  <input value={people[f.index]?.name || ""} onChange={(e) => setPerson(f.index, "name", e.target.value)} data-testid={`person-name-${f.index}`}
                    placeholder="Name" className="flex-1 min-w-[120px] bg-black/30 border-b border-white/15 focus:outline-none px-1 py-1.5 text-white font-mono-tech text-xs" />
                  <select value={people[f.index]?.relationship || ""} onChange={(e) => setPerson(f.index, "relationship", e.target.value)} data-testid={`person-rel-${f.index}`}
                    className="bg-black/40 border border-white/15 px-2 py-1.5 text-white font-mono-tech text-[11px]">
                    {RELATIONSHIPS.map((r) => <option key={r} value={r} className="bg-[#0d0d0f]">{r || "relationship"}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 font-mono-tech text-[10px] text-white/50 cursor-pointer" data-testid={`person-child-${f.index}`}>
                    <input type="checkbox" checked={!!people[f.index]?.is_child} onChange={(e) => setPerson(f.index, "is_child", e.target.checked)} /> Child (age lock)
                  </label>
                  <span className="font-mono-tech text-[9px] text-white/30">~{f.age}y · {f.gender}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 mt-6">
            <NeonButton accent={ACC} onClick={createGroup} disabled={saving || !faces.length} data-testid="create-group-btn">{saving ? "Locking…" : "Lock Group Identities"}</NeonButton>
            <NeonButton onClick={() => setView("list")}>Cancel</NeonButton>
          </div>
        </HoloCard>
      </div>
    );
  }

  // ---- DETAIL / ORCHESTRATOR ----
  const report = result?.report;
  return (
    <div data-testid="group-detail">
      <button onClick={() => { setView("list"); setResult(null); loadGroups(); }} className="flex items-center gap-2 font-mono-tech text-white/50 text-xs mb-6 hover:text-white"><ArrowLeft size={14} /> Back to groups</button>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Controls */}
        <HoloCard accent={ACC} className="p-6">
          <h2 className="font-display font-bold text-xl">{detail.name}</h2>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {detail.people.map((p) => (
              <div key={p.index} className="text-center" title={`${p.name}${p.is_child ? " (child)" : ""}`}>
                {p.crop && <img src={p.crop} alt={p.name} className="w-11 h-11 object-cover border border-white/15" />}
                <div className="font-mono-tech text-[8px] text-white/50 mt-0.5 w-11 truncate">{p.name}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-4">
            <div>
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Mode</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(opts?.modes || ["family"]).map((m) => (
                  <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
                    className="font-mono-tech text-[10px] uppercase px-2 py-1 border transition-colors"
                    style={mode === m ? { borderColor: c.hex, color: c.hex } : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                    {opts?.mode_labels?.[m] || m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Layout</span>
              <select value={layout} onChange={(e) => setLayout(e.target.value)} data-testid="layout-select"
                className="mt-1.5 w-full bg-black/40 border border-white/15 px-2 py-2 text-white font-mono-tech text-[11px]">
                {(opts?.layouts || ["auto"]).map((l) => <option key={l} value={l} className="bg-[#0d0d0f]">{opts?.layout_labels?.[l] || l}</option>)}
              </select>
            </div>
            <div>
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Creative Brief (optional)</span>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="group-prompt" rows={2}
                className="mt-1.5 w-full bg-black/30 border border-white/15 focus:outline-none p-2 text-white font-mono-tech text-xs" placeholder="e.g. outdoor golden-hour holiday card" />
            </div>
            <NeonButton accent={ACC} className="w-full" onClick={generate} disabled={genLoading} data-testid="generate-group-btn">
              {genLoading ? <><Loader2 size={14} className="animate-spin" /> Orchestrating…</> : <><Wand2 size={14} /> Generate Group Portrait · 15 cr</>}
            </NeonButton>
          </div>
        </HoloCard>

        {/* Result + Report */}
        <HoloCard accent={report?.status === "LOCKED" ? "green" : ACC} className="p-6">
          {!result ? (
            <div className="h-full min-h-[300px] flex items-center justify-center text-white/30 font-mono-tech text-xs">Generate a portrait to see the Group Identity Report™</div>
          ) : (
            <div data-testid="group-result">
              <img src={result.image} alt="result" className="w-full object-contain border border-white/10" />
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} style={{ color: report.status === "LOCKED" ? "#39ff14" : "#ff007f" }} />
                  <span className="font-display font-black text-2xl" style={{ color: report.status === "LOCKED" ? "#39ff14" : "#ff007f" }}>{report.group_score}%</span>
                  <span className="font-mono-tech text-[10px] uppercase text-white/50">{report.status} · {report.detected}/{report.expected} people</span>
                </div>
                {result.refunded && <span className="font-mono-tech text-[10px] text-neon-pink flex items-center gap-1"><AlertTriangle size={12} /> Refunded</span>}
              </div>
              {report.warnings?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="group-warnings">
                  {report.warnings.map((w, i) => <span key={i} className="font-mono-tech text-[9px] uppercase border border-neon-pink/40 text-neon-pink px-2 py-0.5">{w}</span>)}
                </div>
              )}
              <div className="space-y-2 mt-4" data-testid="person-scores">
                {report.people.map((p) => {
                  const pidx = detail.people.find((dp) => dp.name === p.name)?.index ?? 0;
                  return (
                  <div key={p.name} className="flex items-center gap-3 border border-white/10 p-2" data-testid={`person-score-${p.name}`}>
                    {p.crop ? <img src={p.crop} alt={p.name} className="w-10 h-10 object-cover" /> : <div className="w-10 h-10 bg-white/5 flex items-center justify-center"><XCircle size={14} className="text-neon-pink" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="font-mono-tech text-xs text-white/80 truncate">{p.name}</span>{p.is_child && <span className="font-mono-tech text-[8px] text-neon-yellow border border-neon-yellow/40 px-1">CHILD</span>}</div>
                      <div className="font-mono-tech text-[9px] text-white/35">{p.relationship || "—"}{p.flags?.length ? ` · ${p.flags[0]}` : ""}</div>
                    </div>
                    <span className="font-display font-bold text-lg" style={{ color: p.status === "LOCKED" ? "#39ff14" : "#ff007f" }}>{p.score}%</span>
                    <button onClick={() => fixPerson(pidx)} disabled={fixing !== null}
                      data-testid={`fix-person-${p.name}`} className="text-white/50 hover:text-neon-yellow disabled:opacity-40" title={`Regenerate ${p.name} only`}>
                      {fixing === pidx ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    </button>
                  </div>
                  );
                })}
              </div>
              <p className="font-mono-tech text-[8px] text-white/25 mt-3">{report.note}</p>
              <NeonButton accent={ACC} className="w-full mt-3" onClick={() => downloadGroupReport({ image: result.image, report, name: detail.name })} data-testid="download-group-report">
                <ShieldCheck size={13} /> Download Group Identity Report
              </NeonButton>
            </div>
          )}
        </HoloCard>
      </div>
    </div>
  );
}
